import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Users,
  Truck,
  Settings,
  LogOut,
  User,
  Bell,
  Menu,
  ChevronDown,
  FileText,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashboardAPI } from "../utils/api";
import { useAuth } from "../hooks/useAuth";
import { TelegramBanner } from "./TelegramBanner";

const Layout = ({ allowedRole }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notiMenuOpen, setNotiMenuOpen] = useState(false);

  // --- REAL Notifications from API ---
  const { data: notiData, isLoading: notiLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => dashboardAPI.notifications(),
    retry: 1,
    refetchInterval: 30000,
  });

  const notifications = notiData?.data?.data || [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  const userDropdownRef = useRef(null);
  const notiDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }
      if (
        notiDropdownRef.current &&
        !notiDropdownRef.current.contains(event.target)
      ) {
        setNotiMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (allowedRole && user?.role !== allowedRole && user?.role !== "admin") {
    navigate("/");
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const markAllAsRead = () => {
    console.log("Mark all as read - API call needed");
  };

  const markAsRead = (id) => {
    console.log("Mark as read:", id, "- API call needed");
  };

  const getNavItems = () => {
    switch (user?.role) {
      case "admin":
        return [
          {
            path: "/admin/dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
          },
          { path: "/admin/orders", label: "Orders", icon: Package },
          { path: "/admin/invoices", label: "Invoices", icon: FileText },
          { path: "/admin/users", label: "Users", icon: Users },
          { path: "/admin/couriers", label: "Couriers", icon: Truck },
          { path: "/admin/settings", label: "Settings", icon: Settings },
        ];
      case "courier":
        return [
          {
            path: "/courier/dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
          },
          { path: "/courier/deliveries", label: "Deliveries", icon: Package },
          { path: "/courier/earnings", label: "Earnings", icon: Truck },
        ];
      case "customer":
        return [
          {
            path: "/customer/dashboard",
            label: "Dashboard",
            icon: LayoutDashboard,
          },
          { path: "/customer/orders", label: "My Orders", icon: Package },
          { path: "/customer/new-order", label: "New Order", icon: Truck },
          { path: "/customer/invoices", label: "Invoices", icon: FileText },
        ];
      default:
        return [];
    }
  };

  const getSidebarTitle = () => {
    switch (user?.role) {
      case "admin":
        return "Admin Area";
      case "courier":
        return "Courier Hub";
      case "customer":
        return "Customer Portal";
      default:
        return "CourierPro";
    }
  };

  const navItems = getNavItems();
  const sidebarTitle = getSidebarTitle();
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        <div className="h-full flex flex-col">
          <div className="h-16 flex items-center px-6 border-b border-gray-200">
            <Package className="w-8 h-8 text-primary-600 mr-3" />
            <span className="text-xl font-bold text-gray-900">
              {sidebarTitle}
            </span>
          </div>

          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors
                    ${
                      isActive(item.path)
                        ? "bg-primary-50 text-primary-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-6 h-6 text-gray-600" />
          </button>

          <div className="hidden lg:block" />

          <div className="flex items-center space-x-4">
            <div className="relative" ref={notiDropdownRef}>
              <button
                onClick={() => setNotiMenuOpen(!notiMenuOpen)}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>

              {notiMenuOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 origin-top-right">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                    <span className="font-semibold text-sm text-gray-900">
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-64 overflow-y-auto divide-y divide-gray-50">
                    {notiLoading ? (
                      <div className="p-4 text-sm text-center text-gray-400">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin mx-auto mb-2"></div>
                        Loading...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-4 text-sm text-center text-gray-400">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((noti) => (
                        <div
                          key={noti.id}
                          onClick={() => markAsRead(noti.id)}
                          className={`p-3 text-left transition-colors cursor-pointer flex items-start space-x-2.5 hover:bg-gray-50 ${!noti.read ? "bg-primary-50/40" : ""}`}
                        >
                          <div
                            className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${!noti.read ? "bg-primary-500" : "bg-transparent"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs text-gray-800 break-words ${!noti.read ? "font-medium" : ""}`}
                            >
                              {noti.text ||
                                noti.message ||
                                noti.title ||
                                "New notification"}
                            </p>
                            <span className="text-[10px] text-gray-400 mt-1 block">
                              {noti.time || noti.created_at || "Just now"}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-gray-200"></div>

            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-3 p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900 leading-none">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize mt-1 leading-none">
                    {user?.role}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 origin-top-right">
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <User className="w-4 h-4 mr-2 text-gray-400" />
                    Profile Settings
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>

        <TelegramBanner />
      </div>
    </div>
  );
};

export default Layout;
